/**
 * 分包记录面板组件
 */
import React, { useState, useEffect } from 'react';
import { Table, Spin, Empty, message, Tag, Button, Drawer, Descriptions, Space, Typography, Skeleton } from 'antd';
import { EyeOutlined, PhoneOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import api from '../../../services/api';
import { formatMoney } from '../../../utils/budgetUtils';
import { SubcontractorService, type SubcontractorDetail, type SubcontractorStatus } from '../../../services/subcontractorService';

const { Text } = Typography;

interface SubcontractPanelProps {
  projectId: string;
  isMobile?: boolean;
  active?: boolean;
}

interface SubcontractorInfo {
  _id: string;
  name: string;
  shortName?: string;
  contactPerson?: string;
  phone?: string;
  status?: SubcontractorStatus;
}

interface SubcontractRecord {
  _id: string;
  costDate: string;
  contractNo?: string;
  contractName: string;
  contractSignDate?: string;
  contractAmount: number;
  paidAmount: number;
  contractOwner?: string;
  contractSigner?: string;
  counterpartyName?: string;
  counterpartySigner?: string;
  subcontractorId?: string | SubcontractorInfo;
  status: 'active' | 'completed' | 'cancelled';
  description?: string;
}

interface SubcontractResponse {
  success: boolean;
  data?: SubcontractRecord[];
  pagination?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

const getStatusTag = (status: SubcontractRecord['status']) => {
  if (status === 'completed') return <Tag color="green">已完成</Tag>;
  if (status === 'cancelled') return <Tag color="red">已取消</Tag>;
  return <Tag color="blue">进行中</Tag>;
};

const getSubcontractorName = (record: SubcontractRecord) => {
  if (!record.subcontractorId) return '待补充';
  return typeof record.subcontractorId === 'string' ? record.subcontractorId : record.subcontractorId.name;
};

const getSubcontractorStatusTag = (status?: SubcontractorStatus) => {
  if (status === 'blacklisted') return <Tag color="red">黑名单</Tag>;
  if (status === 'inactive') return <Tag>停用</Tag>;
  if (status === 'active') return <Tag color="green">活跃</Tag>;
  return <Tag>待补充</Tag>;
};

const SubcontractPanel: React.FC<SubcontractPanelProps> = ({
  projectId,
  isMobile = false,
  active = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<SubcontractRecord[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: isMobile ? 5 : 10,
    total: 0,
  });
  const [loaded, setLoaded] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [subcontractorDetail, setSubcontractorDetail] = useState<SubcontractorDetail | null>(null);

  const fetchData = async (page = 1, pageSize = isMobile ? 5 : 10) => {
    if (!projectId) return;

    setLoading(true);
    try {
      const response = await api.get('/subcontract-costs', {
        params: {
          projectId,
          page,
          limit: pageSize,
        },
      }) as SubcontractResponse;

      if (response.success && response.data) {
        setData(response.data);
        setPagination({
          current: response.pagination?.page || page,
          pageSize: response.pagination?.limit || pageSize,
          total: response.pagination?.total || 0,
        });
      }
    } catch (error) {
      console.error('获取分包记录失败:', error);
      message.error('获取分包记录失败');
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  useEffect(() => {
    if (active && !loaded) {
      fetchData();
    }
  }, [active, loaded, projectId]);

  const openSubcontractorDetail = async (record: SubcontractRecord) => {
    const subcontractorId = typeof record.subcontractorId === 'string' ? undefined : record.subcontractorId?._id;
    if (!subcontractorId) {
      message.info('该记录暂未关联可查看的分包商详情');
      return;
    }

    setDetailVisible(true);
    setDetailLoading(true);
    try {
      const res = await SubcontractorService.getSubcontractorById(subcontractorId);
      if (res.success && res.data) {
        setSubcontractorDetail(res.data);
      }
    } catch (error: any) {
      message.error(error?.response?.data?.message || error?.response?.data?.error || '加载分包商详情失败');
    } finally {
      setDetailLoading(false);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return dateStr.split('T')[0];
  };

  const columns: ColumnsType<SubcontractRecord> = isMobile ? [
    {
      title: '合同/日期',
      key: 'info',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{record.contractName}</div>
          <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
            {record.contractNo || '待补充编号'} · {getSubcontractorName(record)}
          </div>
          <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
            {formatDate(record.costDate)} · {getStatusTag(record.status)}
          </div>
          {typeof record.subcontractorId !== 'string' && record.subcontractorId?.status && (
            <div style={{ marginTop: 4 }}>{getSubcontractorStatusTag(record.subcontractorId.status)}</div>
          )}
          <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
            责任人：{record.contractOwner || '待补充'}
          </div>
          {record.description && (
            <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
              {record.description}
            </div>
          )}
          <Button type="link" size="small" style={{ paddingLeft: 0 }} icon={<EyeOutlined />} onClick={() => openSubcontractorDetail(record)}>
            查看分包商详情
          </Button>
        </div>
      ),
    },
    {
      title: '金额',
      key: 'amount',
      width: 120,
      align: 'right',
      render: (_, record) => (
        <div>
          <div>{formatMoney(record.contractAmount, false)}</div>
          <div style={{ fontSize: 11, color: '#666' }}>已付 {formatMoney(record.paidAmount, false)}</div>
        </div>
      ),
    },
  ] : [
    {
      title: '日期',
      dataIndex: 'costDate',
      key: 'costDate',
      width: 120,
      render: (val: string) => formatDate(val),
    },
    {
      title: '分包商',
      key: 'subcontractor',
      width: 240,
      render: (_, record) => {
        const info = typeof record.subcontractorId === 'string' ? null : record.subcontractorId;
        return (
          <Space direction="vertical" size={2}>
            <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => openSubcontractorDetail(record)}>
              {getSubcontractorName(record)}
            </Button>
            <Space size={6} wrap>
              {info?.shortName ? <Text type="secondary">{info.shortName}</Text> : null}
              {getSubcontractorStatusTag(info?.status)}
            </Space>
            <Text type="secondary">
              <PhoneOutlined /> {info?.contactPerson || '待补充联系人'} / {info?.phone || '待补充电话'}
            </Text>
          </Space>
        );
      },
    },
    {
      title: '合同编号',
      dataIndex: 'contractNo',
      key: 'contractNo',
      width: 140,
      render: (val?: string) => val || '待补充',
    },
    {
      title: '合同名称',
      dataIndex: 'contractName',
      key: 'contractName',
      width: 180,
      ellipsis: true,
    },
    {
      title: '合同状态',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (val: SubcontractRecord['status']) => getStatusTag(val),
    },
    {
      title: '合同金额',
      dataIndex: 'contractAmount',
      key: 'contractAmount',
      width: 130,
      align: 'right',
      render: (val: number) => formatMoney(val, false),
    },
    {
      title: '已付金额',
      dataIndex: 'paidAmount',
      key: 'paidAmount',
      width: 130,
      align: 'right',
      render: (val: number) => formatMoney(val, false),
    },
    {
      title: '责任人',
      dataIndex: 'contractOwner',
      key: 'contractOwner',
      width: 110,
      render: (val?: string) => val || '待补充',
    },
    {
      title: '备注',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (val: string) => val || '-',
    },
  ];

  if (!loaded && !active) {
    return null;
  }

  if (loading && !data.length) {
    return (
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <Spin tip="加载中..." />
      </div>
    );
  }

  return (
    <>
      {!data.length ? (
        <Empty description="暂无分包记录" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <Table
          columns={columns}
          dataSource={data}
          rowKey="_id"
          loading={loading}
          size="small"
          pagination={{
            ...pagination,
            size: 'small',
            showSizeChanger: false,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => fetchData(page, pageSize),
          }}
        />
      )}

      <Drawer
        title="分包商详情"
        open={detailVisible}
        onClose={() => {
          setDetailVisible(false);
          setSubcontractorDetail(null);
        }}
        width={520}
        destroyOnClose
      >
        {detailLoading ? (
          <Skeleton active paragraph={{ rows: 8 }} />
        ) : subcontractorDetail ? (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="分包商名称">{subcontractorDetail.name}</Descriptions.Item>
            <Descriptions.Item label="简称">{subcontractorDetail.shortName || '—'}</Descriptions.Item>
            <Descriptions.Item label="联系人">{subcontractorDetail.contactPerson}</Descriptions.Item>
            <Descriptions.Item label="联系电话">{subcontractorDetail.phone}</Descriptions.Item>
            <Descriptions.Item label="邮箱">{subcontractorDetail.email || '—'}</Descriptions.Item>
            <Descriptions.Item label="状态">{getSubcontractorStatusTag(subcontractorDetail.status)}</Descriptions.Item>
            <Descriptions.Item label="合作项目数">{subcontractorDetail.totalProjects || 0}</Descriptions.Item>
            <Descriptions.Item label="合作总金额">{formatMoney(subcontractorDetail.totalAmount)}</Descriptions.Item>
            <Descriptions.Item label="平均评分">{subcontractorDetail.averageEvaluation ? `${subcontractorDetail.averageEvaluation.toFixed(1)} / 5` : '—'}</Descriptions.Item>
            <Descriptions.Item label="地址">{subcontractorDetail.address || '—'}</Descriptions.Item>
            <Descriptions.Item label="黑名单原因">{subcontractorDetail.blacklistReason || '—'}</Descriptions.Item>
          </Descriptions>
        ) : (
          <Empty description="暂无详情数据" />
        )}
      </Drawer>
    </>
  );
};

export default SubcontractPanel;
